// White-label prequalification — FLOW-LEVEL brand resolution.
//
// Resolution is lifted here so it happens once at the boundary of the
// prequalification flow and every descendant surface inherits it. The
// per-route hook it replaces made branding opt-in forever: the next screen
// added to this flow was unbranded by default, and nobody noticed until a
// partner did. That is exactly how the result screen came to render Phoenix
// at the end of a branded assisted application.
//
// ── THIS FILE RENDERS NO DOM ─────────────────────────────────────────────
//
// Lifting the RESOLUTION is the fix; lifting the WRAPPER would be a bug. The
// `<div data-partner-theme>` that carries the --pq-* variables stays exactly
// where it is, inside the funnel page, because the assisted surface's operator
// strip is a SIBLING ABOVE it and must inherit no partner colour or font — it
// is Phoenix's own control over Phoenix's record of who attested to what, and a
// staff control wearing the partner's skin is one the operator can misread.
// That guarantee is a DOM-containment assertion, which is precisely why a
// context provider may legally sit above the strip and a wrapper element may
// not. See assistedPrequal.test.tsx.
//
// ── NEVER A STALE BRAND ──────────────────────────────────────────────────
//
// State is KEYED ON THE REQUESTED SLUG and a brand is emitted only when the two
// agree. The hook this replaces did `setState(s => ({...s, loading: true}))`,
// which retained the previous brand across a change of slug — so navigating
// between two partner funnels (the same route pattern, so no remount) painted
// partner A's full skin on partner B's URL for the length of the fetch. The
// keying makes that structurally impossible rather than merely unlikely.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Outlet, useParams } from "react-router-dom";
import { fetchPartnerBrand, type PartnerBrandSkin } from "../../api/partnerBrand";
import {
  fetchPrequalFunnelCopy,
  type PrequalFunnelCopy,
} from "../../api/prequalFunnelCopy";
import { FUNNEL_COPY_FALLBACK, withFunnelQuestionDefaults } from "./prequalContent";

/** Role/step segments that live directly under /prequalify — never a slug. */
const RESERVED_SLUGS = new Set(["buyer", "seller", "explore", "result"]);

export function isPartnerSlug(slug: string | null | undefined): slug is string {
  return !!slug && !RESERVED_SLUGS.has(slug.toLowerCase());
}

export interface PartnerBrandState {
  /** The resolved partner skin, or null for the native (Phoenix) funnel. */
  brand: PartnerBrandSkin | null;
  /** True while a partner brand is being fetched. Chrome must hold rather than
   *  paint Phoenix on a route that is going to resolve to a partner. */
  loading: boolean;
  /** True when a partner slug was given but resolved to a 404 (unknown /
   *  inactive) — the funnel renders native + a subtle fallback notice. */
  notFound: boolean;
}

interface PrequalBrandContextValue extends PartnerBrandState {
  /** The slug this provider was ASKED for (null = native). A nested provider
   *  compares against it to adopt an ancestor's answer rather than issue a
   *  second identical request for the same brand. */
  requested: string | null;
  /** The funnel's consent + fine print, ALWAYS populated — see
   *  {@link useFunnelCopy}. Carried on the same context so one provider means one
   *  fetch for the whole flow. */
  funnelCopy: PrequalFunnelCopy;
}

const PrequalBrandContext = createContext<PrequalBrandContextValue | null>(null);

// Frozen so the native path returns a stable identity and can never be mistaken
// for a fresh object each render.
const NATIVE: PartnerBrandState = Object.freeze({
  brand: null,
  loading: false,
  notFound: false,
});

const PENDING: PartnerBrandState = Object.freeze({
  brand: null,
  loading: true,
  notFound: false,
});

interface Resolved {
  /** The slug `brand`/`notFound` belong to. Never trusted unless it matches
   *  what is currently being requested. */
  slug: string | null;
  brand: PartnerBrandSkin | null;
  notFound: boolean;
}

/**
 * The resolution engine. Given a requested slug (null = native), produce state.
 *
 * <b>Native is synchronous on the FIRST FRAME</b> — `requested === null` returns
 * `loading: false` before any effect runs and issues no request at all. That is
 * load-bearing: a Phoenix-native application must not acquire a round trip, a
 * skeleton, or a frame of delay to fix a partner-only problem.
 */
function useBrandResolution(requested: string | null): PartnerBrandState {
  const [resolved, setResolved] = useState<Resolved>(() => ({
    slug: null,
    brand: null,
    notFound: false,
  }));

  useEffect(() => {
    // Native — nothing to look up, so nothing is looked up.
    if (requested === null) return;
    let alive = true;
    fetchPartnerBrand(requested)
      .then((skin) => {
        if (!alive) return;
        // D2a — a buyer team's site answers 200 with `usesPhoenixSkin: true`.
        // That is a NATIVE render: no brand for the theme / chrome to paint,
        // and NOT a not-found (the slug resolved; the pages send it from the
        // route on finalize, so the team is still credited). Normalised HERE,
        // at the one resolution point, so every consumer of usePartnerBrand
        // sees exactly what a Phoenix-native route sees.
        const nativeSkin = skin?.usesPhoenixSkin === true;
        setResolved({
          slug: requested,
          brand: nativeSkin ? null : skin,
          notFound: skin === null,
        });
      })
      .catch(() => {
        // Transient failure — fall back silently to the native funnel. NO
        // "we couldn't find that partner" notice, which is reserved for a 404
        // and would otherwise fire on a flaky network.
        if (alive) setResolved({ slug: requested, brand: null, notFound: false });
      });
    return () => {
      alive = false;
    };
  }, [requested]);

  if (requested === null) return NATIVE;
  // The keying. A brand resolved for a DIFFERENT slug is not this surface's
  // brand, and showing it would put one partner's mark on another's funnel.
  if (resolved.slug !== requested) return PENDING;
  return { brand: resolved.brand, loading: false, notFound: resolved.notFound };
}

/**
 * The funnel's consent + legal fine print, resolved from the server's PUBLISHED
 * copy and overlaid on the compiled-in defaults.
 *
 * <b>Never pending, never partial.</b> It starts at {@link FUNNEL_COPY_FALLBACK} —
 * the same words the funnel shipped with — and swaps to the server's payload only
 * once a whole payload arrives. There is deliberately no `loading` state and nothing
 * for a caller to branch on: the alternative is a finalize step that paints a
 * checkbox with no consent sentence beside it for a frame, or worse, blocks
 * finalizing on a copy fetch. An empty override table returns the same words as the
 * fallback, so a store nobody has published to is byte-identical to today.
 *
 * <b>Slug-free, unlike the brand resolve above.</b> Every funnel block is
 * Phoenix-tier-only server-side, so there is nothing to key on and this runs on the
 * native funnel too — which is the point: the brand fetch does NOT run there.
 *
 * The fetcher swallows every failure, so there is no catch arm here to get wrong.
 */
function useFunnelCopyResolution(active: boolean): PrequalFunnelCopy {
  const [copy, setCopy] = useState<PrequalFunnelCopy | null>(null);

  useEffect(() => {
    // Inert when an ancestor already holds the answer — no duplicate request.
    if (!active) return;
    let alive = true;
    fetchPrequalFunnelCopy().then((resolved) => {
      // A null resolve is any failure at all. Keep the fallback; say nothing.
      // The question map is laid over its compiled defaults, so a key the
      // payload lacks still renders (a blank one stays blank — D4).
      if (alive && resolved) {
        setCopy({ ...resolved, questions: withFunnelQuestionDefaults(resolved.questions) });
      }
    });
    return () => {
      alive = false;
    };
  }, [active]);

  return copy ?? FUNNEL_COPY_FALLBACK;
}

export interface PrequalBrandProviderProps {
  /**
   * Where the site comes from, when it is NOT the address bar.
   *
   * <b>The tri-state is load-bearing.</b>
   * <ul>
   *   <li><b>`undefined`</b> — no source given: resolve from the route `:slug`.
   *       The public funnel's exact behaviour, one strict check away from any
   *       of this.</li>
   *   <li><b>`null`</b> — this application HAS no site. A first-class state (a
   *       Phoenix-run application), which renders native and fetches
   *       nothing.</li>
   *   <li><b>a string</b> — resolve it. A malformed or reserved value degrades
   *       to native by the same guard the route uses: fail to Phoenix, never to
   *       a half-applied skin.</li>
   * </ul>
   *
   * The assisted surfaces need this: `/…/assist/{draftId}` is addressed by draft
   * id and carries no `:slug` at all, so a route-only resolver returns native
   * for every assisted application — including one a broker started from their
   * own branded site, with the client watching. The result screen needs it for
   * the same reason, and reads it from the finalized result, which is the brand
   * the submission was ATTRIBUTED to.
   */
  source?: string | null;
  /** Omitted on a layout route, where the matched child renders through
   *  `<Outlet/>`. Passed when a surface wraps its own subtree. */
  children?: ReactNode;
}

/**
 * Resolve the partner brand for everything beneath it.
 *
 * Renders NO DOM — see the file header.
 *
 * <b>Nests.</b> A provider whose `source` names the same slug an ancestor is
 * already resolving ADOPTS that answer instead of issuing a second identical
 * request. So a branded public funnel navigating to its own result screen
 * resolves the brand once for the whole journey, and the result screen's own
 * (attributed) source costs nothing when it agrees with the URL — which, on the
 * public funnel, it always does.
 */
export function PrequalBrandProvider({ source, children }: PrequalBrandProviderProps) {
  const { slug: routeSlug } = useParams();
  const parent = useContext(PrequalBrandContext);

  // ONE guard over whichever source is in play, so an explicit source can never
  // reach the network by a path the route slug would have been rejected on.
  const candidate = source !== undefined ? source : routeSlug;
  const requested = isPartnerSlug(candidate) ? candidate : null;

  const adopt = parent !== null && parent.requested === requested;
  // Inert while adopting: passing null means no effect, no fetch, no duplicate
  // request for a brand an ancestor already holds.
  const own = useBrandResolution(adopt ? null : requested);

  // Funnel copy is adopted from ANY ancestor provider, not only one whose slug
  // agrees: it is slug-free, so every provider in the tree would resolve the same
  // payload and a second fetch would buy nothing. (Brand adoption has to compare
  // slugs; this deliberately does not.)
  const ownFunnelCopy = useFunnelCopyResolution(parent === null);
  const funnelCopy = parent?.funnelCopy ?? ownFunnelCopy;

  const value = useMemo<PrequalBrandContextValue>(
    () =>
      adopt && parent !== null
        ? parent
        : { ...own, requested, funnelCopy },
    [adopt, parent, own, requested, funnelCopy],
  );

  return (
    <PrequalBrandContext.Provider value={value}>
      {children ?? <Outlet />}
    </PrequalBrandContext.Provider>
  );
}

/**
 * The partner brand for the current surface.
 *
 * Takes NO argument. A surface says what it renders; it does not also decide
 * where the brand comes from — that is the provider's single job, and keeping
 * it there is what stops the next screen added to this flow from being
 * unbranded by default.
 *
 * <b>Falls back to resolving from the route when no provider is mounted</b>, so
 * a surface rendered on its own (as several tests do) behaves exactly as it did
 * before the provider existed. The fallback is inert whenever a provider IS
 * present — it is handed null, so it fetches nothing.
 */
export function usePartnerBrand(): PartnerBrandState {
  const ctx = useContext(PrequalBrandContext);
  const { slug } = useParams();
  const routeSlug = isPartnerSlug(slug) ? slug : null;
  const standalone = useBrandResolution(ctx === null ? routeSlug : null);
  return ctx ?? standalone;
}

/**
 * The funnel's consent + fine print for the current surface. ALWAYS populated.
 *
 * Every field is a real sentence on the very first frame (the compiled-in default),
 * replaced by the server's published copy when it lands — there is no null to
 * guard and no loading state to branch on. A field the server publishes BLANK
 * ("" or whitespace) is passed through as-is, because "show nothing" is what an
 * empty block means (D4): callers test it with `hasText` (`./blankCopy`) and drop
 * the element and its framing rather than render it empty.
 *
 * <b>Resolves standalone when no provider is mounted</b>, mirroring
 * {@link usePartnerBrand}, so a surface rendered on its own (several tests, and any
 * funnel view not yet under the provider) still picks up a published edit rather
 * than silently keeping the compiled default forever. Inert whenever a provider IS
 * present, so the flow costs one fetch.
 */
export function useFunnelCopy(): PrequalFunnelCopy {
  const ctx = useContext(PrequalBrandContext);
  const standalone = useFunnelCopyResolution(ctx === null);
  return ctx?.funnelCopy ?? standalone;
}
